import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Space,
  Tag,
  Popconfirm,
  Typography,
  Tooltip,
  InputNumber,
  Progress,
  Select,
  DatePicker,
  Empty,
} from 'antd';
import {
  PlusOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  KeyOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import llmService from '../../services/llmService';
import TagsInput, { isIpOrCidr } from '../../../shared/components/TagsInput';

const { Text } = Typography;

// 配额转换为美元 (500000 单位 = $1)
const quotaToDollar = (quota) => {
  if (quota === undefined || quota === null) return '$0.00';
  const dollars = quota / 500000;
  if (dollars >= 1000) {
    return `$${(dollars / 1000).toFixed(2)}K`;
  }
  if (dollars >= 0.01) {
    return `$${dollars.toFixed(2)}`;
  }
  return `$${dollars.toFixed(4)}`;
};

const Tokens = () => {
  const { t } = useTranslation();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingToken, setEditingToken] = useState(null);
  const [newTokenKey, setNewTokenKey] = useState(null);
  const [showKeys, setShowKeys] = useState({});
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [unlimitedQuota, setUnlimitedQuota] = useState(false);
  const [modelLimitsEnabled, setModelLimitsEnabled] = useState(false);
  const [groups, setGroups] = useState({});
  const [groupsLoading, setGroupsLoading] = useState(false);

  useEffect(() => {
    fetchTokens();
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    setGroupsLoading(true);
    try {
      const response = await llmService.getGroups();
      if (response.success && response.data) {
        setGroups(response.data);
      }
    } catch (error) {
      // silent fail
    } finally {
      setGroupsLoading(false);
    }
  };

  const fetchTokens = async () => {
    setLoading(true);
    try {
      const response = await llmService.getTokens();
      if (response.success) {
        setTokens(response.data?.items || []);
      } else {
        message.error(response.error || t('Failed to retrieve keys'));
      }
    } catch (error) {
      message.error(t('Failed to retrieve keys'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingToken(null);
    form.resetFields();
    setUnlimitedQuota(false);
    setModelLimitsEnabled(false);
    setModalVisible(true);
  };

  const handleEdit = (token) => {
    setEditingToken(token);
    setUnlimitedQuota(token.unlimited_quota || false);
    setModelLimitsEnabled(token.model_limits_enabled || false);
    form.setFieldsValue({
      name: token.name,
      unlimited_quota: token.unlimited_quota || false,
      remain_quota: token.remain_quota || 0,
      group: token.group || '',
      model_limits_enabled: token.model_limits_enabled || false,
      model_limits: token.model_limits || '',
      allow_ips: token.allow_ips || '',
      expired_time: token.expired_time && token.expired_time !== -1
        ? dayjs(token.expired_time * 1000)
        : null,
    });
    setModalVisible(true);
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      // 处理过期时间
      const submitData = { ...values };
      if (submitData.expired_time) {
        submitData.expired_time = Math.floor(submitData.expired_time.valueOf() / 1000);
      } else {
        submitData.expired_time = -1; // 永不过期
      }

      // 判断是否需要自动启用
      let shouldAutoEnable = false;
      if (editingToken && editingToken.status !== 1) {
        const now = Math.floor(Date.now() / 1000);
        if (submitData.expired_time === -1 || submitData.expired_time > now) {
          shouldAutoEnable = true;
        }
      }

      let response;
      if (editingToken) {
        response = await llmService.updateToken(editingToken.id, submitData);
      } else {
        response = await llmService.createToken(submitData);
      }

      if (response.success) {
        // 如果需要自动启用，额外调用状态更新接口
        if (shouldAutoEnable) {
          try {
            await llmService.updateTokenStatus(editingToken.id, 1);
          } catch (e) {
            // 静默失败，主操作已成功
          }
        }

        message.success(editingToken ? t('Updated successfully') : t('Created successfully'));
        setModalVisible(false);

        if (!editingToken && response.data?.key) {
          setNewTokenKey(response.data.key);
        }

        fetchTokens();
      } else {
        message.error(response.error || t('Operation failed'));
      }
    } catch (error) {
      message.error(error.error || t('Operation failed'));
    } finally {
      setSubmitting(false);
    }
  };

  // 切换 Token 启用/禁用状态
  const handleToggleStatus = async (record) => {
    try {
      const newStatus = record.status === 1 ? 2 : 1; // 1=启用, 2=禁用
      const response = await llmService.updateTokenStatus(record.id, newStatus);
      if (response.success) {
        message.success(newStatus === 1 ? t('Enabled') : t('Disabled'));
        fetchTokens();
      } else {
        message.error(response.error || t('Operation failed'));
      }
    } catch (error) {
      message.error(error.error || t('Operation failed'));
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await llmService.deleteToken(id);
      if (response.success) {
        message.success(t('Deleted successfully'));
        fetchTokens();
      } else {
        message.error(response.error || t('Deletion failed'));
      }
    } catch (error) {
      message.error(error.error || t('Deletion failed'));
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        message.success(t('Copied to clipboard'));
      }).catch(() => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      message.success(t('Copied to clipboard'));
    } catch (err) {
      message.error(t('Failed to copy. Please copy manually.'));
    }
    document.body.removeChild(textarea);
  };

  // 列表里的 key 是打码的；显示/复制时按需取完整 key，取过的缓存在本页
  const [fullKeys, setFullKeys] = useState({});
  const loadFullKey = async (id) => {
    if (fullKeys[id]) return fullKeys[id];
    try {
      const res = await llmService.getTokenKey(id);
      const key = res?.data?.key;
      if (!key) throw new Error(res?.error || res?.message);
      setFullKeys(prev => ({ ...prev, [id]: key }));
      return key;
    } catch (e) {
      message.error(e?.error || e?.message || t('Operation failed'));
      return null;
    }
  };
  const toggleKeyVisibility = async (id) => {
    if (!showKeys[id] && !(await loadFullKey(id))) return;
    setShowKeys(prev => ({ ...prev, [id]: !prev[id] }));
  };
  const copyFullKey = async (id) => {
    const key = await loadFullKey(id);
    if (key) copyToClipboard(`sk-${key}`);
  };

  // 状态渲染
  const renderStatus = (status) => {
    const statusMap = {
      1: { color: 'green', icon: <CheckCircleOutlined />, text: t('Enabled') },
      2: { color: 'red', icon: <CloseCircleOutlined />, text: t('Disabled') },
      3: { color: 'orange', icon: <ClockCircleOutlined />, text: t('Expired') },
      4: { color: 'default', icon: <StopOutlined />, text: t('Exhausted') },
    };
    const s = statusMap[status] || { color: 'default', text: t('Unknown') };
    return <Tag color={s.color} icon={s.icon}>{s.text}</Tag>;
  };

  // 配额渲染
  const renderQuota = (record) => {
    if (record.unlimited_quota) {
      return <Tag color="blue">{t('Unlimited allowance')}</Tag>;
    }
    const used = parseInt(record.used_quota) || 0;
    const remain = parseInt(record.remain_quota) || 0;
    const total = used + remain;
    const percent = total > 0 ? (remain / total) * 100 : 0;

    const getColor = (pct) => {
      if (pct <= 10) return '#ff4d4f';
      if (pct <= 30) return '#faad14';
      return '#52c41a';
    };

    return (
      <Tooltip title={
        <div>
          <div>{t('Used: {{quota}}', { quota: quotaToDollar(used) })}</div>
          <div>{t('Remaining: {{quota}}', { quota: quotaToDollar(remain) })}</div>
          <div>{t('Total: {{quota}}', { quota: quotaToDollar(total) })}</div>
        </div>
      }>
        <div style={{ minWidth: 120 }}>
          <div style={{ fontSize: 12, marginBottom: 2 }}>
            {quotaToDollar(remain)} / {quotaToDollar(total)}
          </div>
          <Progress
            percent={percent}
            size="small"
            strokeColor={getColor(percent)}
            format={() => `${percent.toFixed(0)}%`}
          />
        </div>
      </Tooltip>
    );
  };

  // 模型限制渲染
  const renderModelLimits = (record) => {
    if (!record.model_limits_enabled || !record.model_limits) {
      return <Tag>{t('Unlimited')}</Tag>;
    }
    const models = record.model_limits.split(',').filter(Boolean);
    if (models.length <= 2) {
      return models.map((m, i) => <Tag key={i} color="blue">{m}</Tag>);
    }
    return (
      <Tooltip title={models.join(', ')}>
        <Space>
          <Tag color="blue">{models[0]}</Tag>
          <Tag>+{models.length - 1}</Tag>
        </Space>
      </Tooltip>
    );
  };

  // IP 限制渲染
  const renderAllowIps = (allowIps) => {
    if (!allowIps || allowIps.trim() === '') {
      return <Tag>{t('Unlimited')}</Tag>;
    }
    const ips = allowIps.split('\n').map(ip => ip.trim()).filter(Boolean);
    if (ips.length === 1) {
      return <Tag>{ips[0]}</Tag>;
    }
    return (
      <Tooltip title={ips.join(', ')}>
        <Space>
          <Tag>{ips[0]}</Tag>
          <Tag>+{ips.length - 1}</Tag>
        </Space>
      </Tooltip>
    );
  };

  const columns = [
    {
      title: t('Name'),
      dataIndex: 'name',
      key: 'name',
      width: 120,
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: renderStatus,
    },
    {
      title: t('Remaining / Total quota'),
      key: 'quota',
      width: 160,
      render: (_, record) => renderQuota(record),
    },
    {
      title: t('Group'),
      dataIndex: 'group',
      key: 'group',
      width: 100,
      render: (group) => {
        if (group === 'auto') {
          return <Tooltip title={t('Smart circuit breaker: automatically select the best group')}><Tag color="purple">{t('Smart circuit breaker')}</Tag></Tooltip>;
        }
        return group ? <Tag color="cyan">{group}</Tag> : <Tag>{t('Default')}</Tag>;
      },
    },
    {
      title: t('Key'),
      dataIndex: 'key',
      key: 'key',
      width: 220,
      render: (key, record) => {
        const fullKey = `sk-${fullKeys[record.id] || key}`;
        const maskedKey = `sk-${key?.substring(0, 4)}**********${key?.slice(-4)}`;
        const revealed = showKeys[record.id];
        return (
          <Space>
            <Text code style={{ fontSize: 12 }}>{revealed ? fullKey : maskedKey}</Text>
            <Button
              type="text"
              size="small"
              icon={revealed ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              onClick={() => toggleKeyVisibility(record.id)}
            />
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyFullKey(record.id)}
            />
          </Space>
        );
      },
    },
    {
      title: t('Available models'),
      key: 'model_limits',
      width: 150,
      render: (_, record) => renderModelLimits(record),
    },
    {
      title: t('IP restrictions'),
      dataIndex: 'allow_ips',
      key: 'allow_ips',
      width: 120,
      render: renderAllowIps,
    },
    {
      title: t('Created at'),
      dataIndex: 'created_time',
      key: 'created_time',
      width: 160,
      render: (time) => time ? dayjs(time * 1000).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: t('Expiration time'),
      dataIndex: 'expired_time',
      key: 'expired_time',
      width: 160,
      render: (time) => {
        if (time === -1) return <Tag color="green">{t('Never expires')}</Tag>;
        if (!time) return '-';
        const expDate = dayjs(time * 1000);
        const isExpired = expDate.isBefore(dayjs());
        return (
          <Tag color={isExpired ? 'red' : 'blue'}>
            {expDate.format('YYYY-MM-DD HH:mm')}
          </Tag>
        );
      },
    },
    {
      title: t('Actions'),
      key: 'action',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title={record.status === 1 ? t('Disable') : t('Enable')}>
            <Switch
              size="small"
              checked={record.status === 1}
              onChange={() => handleToggleStatus(record)}
            />
          </Tooltip>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title={t('Delete this key?')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('OK')}
            cancelText={t('Cancel')}
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={<><KeyOutlined /> {t('API key management')}</>}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            {t('Create key')}
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={tokens}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1400 }}
          size="small"
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('No API keys yet. Click "Create key" in the top right to get started.')}
              />
            ),
          }}
        />
      </Card>

      <Modal
        title={editingToken ? t('Edit key') : t('Create key')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label={t('Name')}
            rules={[{ required: true, message: t('Please enter a key name') }]}
          >
            <Input placeholder={t('E.g. development environment key')} />
          </Form.Item>

          <Form.Item
            name="group"
            label={t('Group')}
            extra={t('Leave blank to use the default group. Select auto to enable smart circuit breaking.')}
          >
            <Select
              placeholder={t('Select a group')}
              allowClear
              loading={groupsLoading}
              showSearch
            >
              <Select.Option value="">
                <Space>{t('Default group')}<Text type="secondary" style={{ fontSize: 12 }}>{t('(Leave blank)')}</Text></Space>
              </Select.Option>
              {Object.entries(groups).map(([name, info]) => (
                <Select.Option key={name} value={name}>
                  <Space>
                    {name === 'auto' ? t('Smart circuit breaker (auto)') : name}
                    {info.desc && <Text type="secondary" style={{ fontSize: 12 }}>{info.desc}</Text>}
                    {info.ratio && info.ratio !== '自动' && <Text type="secondary" style={{ fontSize: 12 }}>{t('Multiplier: {{ratio}}', { ratio: info.ratio })}</Text>}
                  </Space>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="unlimited_quota"
            label={t('Unlimited quota')}
            valuePropName="checked"
          >
            <Switch onChange={(checked) => setUnlimitedQuota(checked)} />
          </Form.Item>

          {!unlimitedQuota && (
            <Form.Item
              name="remain_quota"
              label={t('Quota limit')}
              extra={t('Quota units: 500000 = "\'$1')}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={100000000000}
                placeholder={t('Enter the quota limit')}
              />
            </Form.Item>
          )}

          <Form.Item
            name="model_limits_enabled"
            label={t('Enable model restrictions')}
            valuePropName="checked"
          >
            <Switch onChange={(checked) => setModelLimitsEnabled(checked)} />
          </Form.Item>

          {modelLimitsEnabled && (
            <Form.Item
              name="model_limits"
              label={t('Available models')}
              
            >
              <TagsInput format="comma" placeholder="gpt-4, claude-3-opus" />
            </Form.Item>
          )}

          <Form.Item
            name="allow_ips"
            label={t('IP restrictions')}
            extra={t('Leave blank for no restrictions')}
          >
            <TagsInput format="lines" placeholder="192.168.1.1, 10.0.0.0/8" validate={(v) => (isIpOrCidr(v) ? null : t('Invalid IP / CIDR'))} />
          </Form.Item>

          <Form.Item
            name="expired_time"
            label={t('Expiration time')}
            extra={t('Leave blank to never expire')}
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              placeholder={t('Select an expiration time')}
              disabledDate={(current) => current && current < dayjs().startOf('day')}
              disabledTime={(current) => {
                if (!current || !current.isSame(dayjs(), 'day')) {
                  return {};
                }
                const now = dayjs();
                return {
                  disabledHours: () => [...Array(now.hour()).keys()],
                  disabledMinutes: (hour) => hour === now.hour() ? [...Array(now.minute()).keys()] : [],
                  disabledSeconds: (hour, minute) => hour === now.hour() && minute === now.minute() ? [...Array(now.second()).keys()] : [],
                };
              }}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {editingToken ? t('Update') : t('Create')}
              </Button>
              <Button onClick={() => setModalVisible(false)} disabled={submitting}>
                {t('Cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('New key created')}
        open={!!newTokenKey}
        onOk={() => setNewTokenKey(null)}
        onCancel={() => setNewTokenKey(null)}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <p>{t('Please copy and securely save your key. It will only be shown once:')}</p>
        <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 4, marginBottom: 16 }}>
          <Text code copyable>{`sk-${newTokenKey}`}</Text>
        </div>
        <p style={{ color: '#ff4d4f' }}>
          {t('Warning: This key will not be shown again. Please copy and save it now.')}
        </p>
      </Modal>
    </div>
  );
};

export default Tokens;
